import React, { useState } from "react";
import Select, { createFilter } from "react-select";
import { useEffect, useRef } from "react";
import { useUpdateEffect } from "../../../hooks/useUpdateEffect";

const CalcMultiSelect = ({
  parentOptions,
  childOptions,
  parentId,
  childId,
  childIsMulti = false,
  styles,
  onChange,
  join,
  placeholder,
  placeholderSub,
  validate,
  validateMsg,
  validateSub,
  validateSubMsg,
  parentIsPerson,
  childIsPerson,
  fullWidth,
  topTitle,
  disabled = false,
}) => {
  const hasDefaultParent = useRef(false);

  const [parents, setParents] = useState([]);
  const [childs, setChilds] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [isClearable] = useState(true);

  var filteredChilds = [];

  if (selectedParent && selectedParent.value) {
    filteredChilds = childs.filter((child) => {
      return child.parentId === selectedParent.value;
    });
  } else {
    // Show all child options when no parent is selected
    filteredChilds = childs;
  }

  useEffect(() => {
    const parentMap = parentOptions.map((parent) => ({
      value: parent.id,
      label: parentIsPerson
        ? `${parent.firstname} ${parent.lastname}`
        : parent.name,
    }));

    setParents(parentMap);

    const childMap = childOptions.map((child) => ({
      value: child.id,
      label: childIsPerson
        ? `${child.firstname} ${child.lastname}`
        : child.name,
      parentId: child[join],
    }));

    setChilds(childMap);

    const foundParent = parentMap.find((parent) => parent.value === parentId);
    const foundChild = childMap.find((child) => child.value === childId);
    setSelectedParent(foundParent || null);
    setSelectedChild(foundChild || null);

    hasDefaultParent.current = true;
  }, [
    parentOptions,
    childOptions,
    parentId,
    childId,
    join,
    parentIsPerson,
    childIsPerson,
  ]);

  useUpdateEffect(() => {
    if (!hasDefaultParent.current) {
      setSelectedChild(null);
    } else {
      hasDefaultParent.current = false;
    }
  }, [selectedParent]);

  // Handle external prop changes separately
  useEffect(() => {
    if (parents.length > 0) {
      const foundParent = parents.find((parent) => parent.value === parentId);
      const currentParentValue = selectedParent?.value;
      if (foundParent?.value !== currentParentValue) {
        setSelectedParent(foundParent || null);
      }
    }
  }, [parentId, parents, selectedParent]);

  useEffect(() => {
    if (childs.length > 0) {
      const foundChild = childs.find((child) => child.value === childId);
      const currentChildValue = selectedChild?.value;
      if (foundChild?.value !== currentChildValue) {
        setSelectedChild(foundChild || null);
      }
    }
  }, [childId, childs, selectedChild]);

  /*
        const onChildChange = (selectedOption) => {
            if (selectedOption) {
                onChange(selectedParent ? selectedParent.value : 0, selectedOption.value);
                setSelectedChild(selectedOption);
            } else {
                onChange(selectedParent ? selectedParent.value : 0, null); 
                setSelectedChild(null);
            }
        };
    */

  const onChildChange = (selectedOption) => {
    if (Array.isArray(selectedOption) && selectedOption.length > 0) {
      const selectedValues = selectedOption.map((option) => option.value);
      // For multi-select, use the first selected option's parent
      const parentIdFromChild = selectedOption[0].parentId;
      const correspondingParent = parents.find((parent) => parent.value === parentIdFromChild);
      
      if (correspondingParent && (!selectedParent || selectedParent.value !== parentIdFromChild)) {
        setSelectedParent(correspondingParent);
        onChange(parentIdFromChild, selectedValues);
      } else {
        onChange(selectedParent ? selectedParent.value : 0, selectedValues);
      }
      setSelectedChild(selectedOption);
    } else if (selectedOption) {
      // Find the parent for this child
      const parentIdFromChild = selectedOption.parentId;
      const correspondingParent = parents.find((parent) => parent.value === parentIdFromChild);
      
      if (correspondingParent && (!selectedParent || selectedParent.value !== parentIdFromChild)) {
        setSelectedParent(correspondingParent);
        onChange(parentIdFromChild, selectedOption.value);
      } else {
        onChange(selectedParent ? selectedParent.value : 0, selectedOption.value);
      }
      setSelectedChild(selectedOption);
    } else {
      onChange(selectedParent ? selectedParent.value : 0, null);
      setSelectedChild(null);
    }
  };

  const onParentChange = (selectedOption) => {
    if (selectedOption) {
      // Check if current child belongs to the new parent
      const childBelongsToParent = selectedChild && selectedChild.parentId === selectedOption.value;
      
      setSelectedParent(selectedOption);
      
      if (childBelongsToParent) {
        // Keep the current child if it belongs to the new parent
        onChange(selectedOption.value, selectedChild.value);
      } else {
        // Clear the child if it doesn't belong to the new parent
        setSelectedChild(null);
        onChange(selectedOption.value, null);
      }
    } else {
      onChange(null);
      setSelectedParent(null);
      setSelectedChild(null);
    }
  };

  const stylesError = {
    control: (provided, state) => ({
      ...provided,
      border: state.isFocused
        ? "1px solid rgb(225 29 72)"
        : "1px solid rgb(225 29 72)",
      boxShadow: state.isFocused ? "none" : "none",
      "&:hover": {
        border: state.isFocused
          ? "1px solid rgb(225 29 72)"
          : "1px solid rgb(225 29 72)",
      },
      minHeight: "35px",
      width: "100%",
      cursor: "pointer",
      borderRadius: "5px",
    }),
  };

  const tileStyles = {
    position: "absolute",
    fontSize: "11px",
    fontWeight: "500",
    color: "var(--muted-text-color)",
    lineHeight: 1,
    marginBottom: "4px",
    top: "-6px",
    zIndex: "1",
    left: "8px",
    padding: "0 3px",
    background: "#fff",
  };

  const tileStylesError = {
    position: "absolute",
    fontSize: "11px",
    fontWeight: "500",
    color: "rgb(225, 29, 72)",
    lineHeight: 1,
    marginBottom: "4px",
    top: "-6px",
    zIndex: "1",
    left: "8px",
    padding: "0 3px",
    background: "#fff",
  };

  return (
    <div
      style={{
        display: "flex",
        gap: fullWidth ? "12px" : "10px",
        flexWrap: fullWidth ? "wrap" : "unset",
      }}
    >
      <div style={{ width: fullWidth ? "100%" : "50%", position: "relative" }}>
        <Select
          styles={validate && validate ? { ...styles, ...stylesError } : styles}
          options={parents}
          value={selectedParent}
          onChange={onParentChange}
          placeholder={placeholder}
          filterOption={createFilter({ ignoreAccents: false })}
          isClearable={isClearable}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          key={`parent-${selectedParent?.value || 'none'}`}
          isSearchable={true}
        />

        {topTitle && (
          <div style={validate && validate ? tileStylesError : tileStyles}>
            {placeholder}
          </div>
        )}

        {validate && validateMsg && (
          <div style={{ color: "rgb(225, 29, 72)", fontSize: "12px" }}>
            {validateMsg}{" "}
          </div>
        )}
      </div>

      <div style={{ width: fullWidth ? "100%" : "50%", position: "relative" }}>
        <Select
          styles={
            validateSub && validateSub ? { ...styles, ...stylesError } : styles
          }
          options={filteredChilds}
          value={selectedChild}
          onChange={onChildChange}
          placeholder={placeholderSub}
          isClearable={isClearable}
          isMulti={childIsMulti}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          key={`child-${selectedChild?.value || 'none'}`}
          isSearchable={true}
        />

        {topTitle && (
          <div
            style={validateSub && validateSub ? tileStylesError : tileStyles}
          >
            {placeholderSub}
          </div>
        )}

        {validateSub && validateSubMsg && (
          <div style={{ color: "rgb(225, 29, 72)", fontSize: "12px" }}>
            {validateSubMsg}{" "}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalcMultiSelect;
